import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { theme } from '@/lib/theme';

interface StyleOption {
  id: string;
  name: string;
  thumbnail: string;
  prompt?: string;
}

interface StyleCarouselProps {
  styles: StyleOption[];
  selectedStyle: string;
  onSelect: (style: StyleOption) => void;
  isDarkMode: boolean;
}

export const StyleCarousel: React.FC<StyleCarouselProps> = ({ styles, selectedStyle, onSelect, isDarkMode }) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollCarousel = (direction: 'left' | 'right') => {
    const container = carouselRef.current;
    if (!container) return;
    const scrollAmount = Math.max(160, container.clientWidth - 80);
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const updateArrows = () => {
    const el = carouselRef.current;
    if (!el) return;
    const atStart = el.scrollLeft <= 0;
    const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth;
    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  };

  useEffect(() => {
    updateArrows();
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateArrows();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div
      className={cn('relative mb-8 backdrop-blur-sm sm:p-4 p-2 rounded-2xl border-none bg-transparent')}
    >
      <div className="relative mb-3">
        <h2
          className="text-2xl instrument-serif-regular select-none inline-block"
          style={{ color: theme.text(isDarkMode) }}
        >
          Choose a style
        </h2>
        <div className="absolute right-0 top-0 bottom-0 flex items-center gap-2">
          <Button
            onClick={() => scrollCarousel('left')}
            variant="ghost"
            size="icon"
            className={cn(
              'rounded-full transition-all duration-200',
              isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-gray-900',
              !canScrollLeft ? 'opacity-0 pointer-events-none' : 'opacity-100',
              'w-8 h-8 min-w-8' // Fixed dimensions to prevent layout shifts
            )}
            style={{
              transform: canScrollLeft ? 'translateX(0)' : 'translateX(-10px)',
              transition: 'opacity 200ms ease, transform 200ms ease'
            }}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => scrollCarousel('right')}
            variant="ghost"
            size="icon"
            className={cn(
              'rounded-full transition-all duration-200',
              isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-gray-900',
              !canScrollRight ? 'opacity-0 pointer-events-none' : 'opacity-100',
              'w-8 h-8 min-w-8' // Fixed dimensions to prevent layout shifts
            )}
            style={{
              transform: canScrollRight ? 'translateX(0)' : 'translateX(10px)',
              transition: 'opacity 200ms ease, transform 200ms ease'
            }}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="flex items-stretch justify-start">
        <div ref={carouselRef} className="flex p-2 gap-4 overflow-x-auto pb-2 scroll-smooth scrollbar-hide w-full">
          {styles.map((style) => (
            <div key={style.id} className="flex-shrink-0">
              <div
                className={cn(
                  'w-[120px] h-[120px] sm:w-36 sm:h-36 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 relative border-2',
                  selectedStyle === style.name ? 'border-primary scale-105' : 'border-transparent hover:border-primary/50'
                )}
                onClick={() => onSelect(style)}
              >
                <img src={style.thumbnail} alt={style.name} className="w-full h-full object-cover" />
              </div>
              <div className="mt-2 text-center select-none" style={{ color: theme.text(isDarkMode) }}>
                <span className="text-sm font-medium">{style.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StyleCarousel;



